import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Wallet, Plus, TrendingUp, TrendingDown, Download, FileDown, Loader2 } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const SellerWallet = ({ user, onLogout }) => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeDescription, setRechargeDescription] = useState("Wallet recharge by admin");
  const [rechargeVia, setRechargeVia] = useState("Cash");
  const [transactionId, setTransactionId] = useState("");
  const [rechargedOn, setRechargedOn] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });

  useEffect(() => {
    fetchSellerData();
  }, [sellerId]);

  useEffect(() => {
    fetchTransactions();
  }, [sellerId, currentPage]);

  const fetchSellerData = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      const sellerData = response.data.find(s => s.seller_id === sellerId);
      if (sellerData) {
        setSeller(sellerData);
      } else {
        toast.error("Seller not found");
        navigate('/sellers');
      }
    } catch (error) {
      toast.error("Failed to fetch seller data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/wallet/admin/${sellerId}/transactions?page=${currentPage}&limit=50`);
      setTransactions(response.data.transactions || []);
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (error) {
      console.error("Failed to fetch transactions");
    }
  };


  const handleDownloadReceipt = async (txn) => {
    setDownloadingReceipt(txn.id);
    try {
      const res = await axios.get(
        `${API}/wallet/admin/${sellerId}/transaction/${txn.id}/receipt`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `receipt_${txn.id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded");
    } catch (error) {
      console.error("Receipt download failed:", error);
      toast.error("Failed to download receipt");
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Fetch all transactions for export (up to 10000)
      const response = await axios.get(`${API}/wallet/admin/${sellerId}/transactions?page=1&limit=10000`);
      const allTransactions = response.data.transactions || response.data;
      
      if (allTransactions.length === 0) {
        toast.error("No transactions to export");
        return;
      }
      
      // Create CSV content
      const headers = ["Date", "Type", "Amount", "Recharge Via", "Transaction ID", "Description", "Balance After", "Recharged By"];
      const csvRows = [headers.join(",")];
      
      allTransactions.forEach(txn => {
        const row = [
          `"${formatToIST(txn.created_at)}"`,
          txn.type === 'credit' ? 'Credit' : 'Debit',
          txn.type === 'credit' ? `+${txn.amount}` : `-${txn.amount}`,
          `"${txn.recharge_via || '-'}"`,
          `"${txn.transaction_id || '-'}"`,
          `"${(txn.description || '').replace(/"/g, '""')}"`,
          txn.balance_after,
          `"${txn.recharged_by || txn.admin_user_name || 'System'}"`
        ];
        csvRows.push(row.join(","));
      });
      
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `wallet_transactions_${sellerId}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${allTransactions.length} transactions successfully`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export transaction history");
    }
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(rechargeAmount);
    if (amount < 100) {
      toast.error("Minimum recharge amount is ₹100");
      return;
    }

    if (!transactionId.trim()) {
      toast.error("Transaction ID/Remark is required");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/wallet/admin/recharge/${sellerId}`, {
        amount,
        description: rechargeDescription,
        recharge_via: rechargeVia,
        transaction_id: transactionId,
        recharged_on: rechargedOn || undefined
      });
      
      toast.success("Wallet recharged successfully!");
      setRechargeAmount("");
      setRechargeDescription("Wallet recharge by admin");
      setRechargeVia("Cash");
      setTransactionId("");
      setCurrentPage(1); // Reset to first page to see new transaction
      fetchSellerData();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recharge wallet");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <div className="text-slate-600">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!seller) {
    return null;
  }

  const balance = seller.wallet_balance || 0;
  const isLowBalance = balance < 0;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/sellers')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sellers
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-slate-800">{seller.brand_name}</h1>
              <p className="text-slate-600">Wallet Management</p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/admin/sellers/${sellerId}/wallet/transactions`)}
            style={{backgroundColor: '#00A699'}}
          >
            View All Transactions
          </Button>
        </div>

        {isLowBalance && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-semibold">⚠️ Low Balance Alert</p>
            <p className="text-red-600 text-sm">Wallet balance is negative. Please recharge to continue services.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Balance Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5" style={{color: '#00A699'}} />
                Current Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <p className="text-sm text-slate-500 mb-2">Available Balance</p>
                <p className={`text-5xl font-bold ${isLowBalance ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{balance.toLocaleString()}
                </p>
              </div>
              <div className="border-t pt-4 mt-4 text-sm text-slate-600">
                <p><strong>Seller ID:</strong> {seller.seller_id}</p>
                <p><strong>Company:</strong> {seller.company_registered_name}</p>
              </div>
            </CardContent>
          </Card>

          {/* Recharge Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" style={{color: '#00A699'}} />
                Recharge Wallet
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRecharge} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Recharge Amount * (Min: ₹100)</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="100"
                    step="0.01"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={rechargeDescription}
                    onChange={(e) => setRechargeDescription(e.target.value)}
                    placeholder="Wallet recharge by admin"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rechargeVia">Recharge Via *</Label>
                  <select
                    id="rechargeVia"
                    value={rechargeVia}
                    onChange={(e) => setRechargeVia(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    required
                  >
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                    <option value="UPI or QR Code (Gpay/Phonepe etc)">UPI or QR Code (Gpay/Phonepe etc)</option>
                    <option value="FOC">FOC</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transactionId">Transaction ID/Remark *</Label>
                  <Input
                    id="transactionId"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Enter transaction ID or remark"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rechargedOn">Recharged On (Optional)</Label>
                  <Input
                    id="rechargedOn"
                    type="datetime-local"
                    value={rechargedOn}
                    onChange={(e) => setRechargedOn(e.target.value)}
                    max={new Date().toISOString().slice(0, 16)}
                  />
                  <p className="text-xs text-slate-500">Leave empty to use current date/time</p>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  style={{backgroundColor: '#00A699'}}
                  disabled={submitting}
                >
                  {submitting ? "Processing..." : "Recharge Wallet"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Transaction History */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Transaction History</CardTitle>
            {pagination.total > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export All ({pagination.total})
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p>No transactions yet</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Recharge Via</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Transaction ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Balance After</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Recharged By</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Receipt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {transactions.map((txn) => (
                        <tr key={txn.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle text-sm text-slate-700">
                            <div className="whitespace-nowrap">
                              <div>{formatToIST(txn.created_at, false)}</div>
                              <div className="text-xs text-slate-500">{formatToIST(txn.created_at).split(', ')[1]}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap">
                            <Badge className={txn.type === 'credit' ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                              {txn.type === 'credit' ? (
                                <><TrendingUp className="w-3 h-3 inline mr-1" />Credit</>
                              ) : (
                                <><TrendingDown className="w-3 h-3 inline mr-1" />Debit</>
                              )}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-right">
                            <span className={`text-sm font-medium ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.type === 'credit' ? '+' : '-'}₹{txn.amount.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-700">
                            {txn.recharge_via || '-'}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-700">
                            {txn.transaction_id || '-'}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-700">
                            {txn.description}
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-right text-sm font-medium text-slate-900">
                            ₹{txn.balance_after?.toLocaleString() || '-'}
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-600">
                            {txn.recharged_by || txn.admin_user_name || 'System'}
                          </td>
                          <td className="px-4 py-3 align-middle text-center">
                            {txn.type === 'credit' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadReceipt(txn)}
                                disabled={downloadingReceipt === txn.id}
                                data-testid={`download-receipt-${txn.id}`}
                                title="Download Receipt"
                              >
                                {downloadingReceipt === txn.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FileDown className="w-4 h-4 text-teal-600" />
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <p className="text-sm text-slate-600">
                      Showing {Math.min((currentPage - 1) * pagination.limit + 1, pagination.total)} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} transactions
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      {[...Array(pagination.pages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Show first, last, and pages around current
                        if (
                          pageNum === 1 ||
                          pageNum === pagination.pages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              style={currentPage === pageNum ? {backgroundColor: '#00A699'} : {}}
                            >
                              {pageNum}
                            </Button>
                          );
                        } else if (
                          pageNum === currentPage - 2 ||
                          pageNum === currentPage + 2
                        ) {
                          return <span key={pageNum} className="px-2">...</span>;
                        }
                        return null;
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === pagination.pages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default SellerWallet;
